'use client';

import { Fragment, ReactNode, useEffect, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';

import { TUIError } from '@northladder/utilities';

import { OTPInputCapture } from './OTPInputCapture';
import { OTPLoaderAndError } from './OTPLoaderAndError';

/**
 * Properties for the OtpModal component.
 */
interface IOtpModalProps {
  /** Error occurred during OTP validation. */
  errorInValidateOTP: TUIError;
  /** Error occurred during OTP sending. */
  errorInSendingOTP: TUIError;
  /** Whether the modal is enabled and should be open. */
  isEnabled: boolean;
  /** Whether the OTP validation is in progress. */
  isValidatingOTP: boolean;
  /** Whether the OTP is currently being sent. */
  isSendingOTP: boolean;
  /** The phone number to which the OTP is sent. */
  phoneNumber: string;
  /** The expected length of the OTP. Defaults to 6. */
  otpLength?: number;
  /** Callback to initiate sending of the OTP. */
  onSendOTP: () => void;
  /** Callback to validate the entered OTP. */
  onValidateOTP: (otp: string) => void;
  /** Function to render the component that triggers the modal. */
  renderModalTrigger: ({
    onOpenModal,
  }: {
    /** Function to open the modal. */
    onOpenModal: () => void;
  }) => ReactNode;
}

export function OtpModal({
  otpLength = 6,
  errorInValidateOTP,
  errorInSendingOTP,
  isEnabled,
  isSendingOTP,
  phoneNumber,
  isValidatingOTP,
  onSendOTP,
  onValidateOTP,
  renderModalTrigger,
}: IOtpModalProps) {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    setIsOpen(isEnabled);
  }, [isEnabled]);

  const handleOnCloseModal = () => {
    setIsOpen(false);
  };

  const handleOpenModal = () => {
    setIsOpen(true);
  };

  return (
    <>
      {renderModalTrigger({ onOpenModal: handleOpenModal })}

      <Transition appear as={Fragment} show={isOpen}>
        <Dialog
          as="div"
          className="relative z-[100]"
          onClose={handleOnCloseModal}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/40" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div
              className="
                flex min-h-full items-center justify-center p-4 text-center
              "
            >
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel
                  className="
                    w-full max-w-sm transform overflow-hidden rounded-2xl
                    bg-white p-6 text-start align-middle shadow-xl transition-all
                  "
                >
                  {isSendingOTP || errorInSendingOTP?.message ? (
                    <OTPLoaderAndError
                      error={errorInSendingOTP}
                      onCloseModal={handleOnCloseModal}
                      onSendOTP={onSendOTP}
                      otpLength={otpLength}
                      phoneNumber={phoneNumber}
                    />
                  ) : (
                    <OTPInputCapture
                      error={errorInValidateOTP}
                      isLoading={isValidatingOTP}
                      onCloseModal={handleOnCloseModal}
                      onSendOTP={onSendOTP}
                      onValidateOTP={onValidateOTP}
                      otpLength={otpLength}
                      phoneNumber={phoneNumber}
                    />
                  )}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
}
